import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../components/common/Modal';
import {
  assayRequestApi,
  AssayType,
  AssayTypeInput,
} from '../../services/assayRequestApi';

const STANDARD_CATEGORIES = ['Enzyme Inhibition', 'Cell-Based', 'Binding', 'ADME', 'Safety'];

const INPUT_MODES = [
  { value: 'dose_response', label: 'Dose response' },
  { value: 'single_point', label: 'Single point' },
  { value: 'single_value', label: 'Single numeric value' },
  { value: 'kinetic', label: 'Kinetic measurement' },
  { value: 'endpoint', label: 'Endpoint measurement' },
  { value: 'binary', label: 'Positive / negative' },
];

const apiErrorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => typeof item === 'string' ? item : item?.msg)
      .filter(Boolean);
    if (messages.length) return messages.join('; ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return fallback;
};

const EMPTY_FORM: AssayTypeInput = {
  name: '',
  category: 'Enzyme Inhibition',
  description: '',
  typical_units: '',
  input_mode: 'dose_response',
  is_active: true,
  internal_code: '',
  target_name: '',
};

type AssayTypesPageProps = {
  registrationOnly?: boolean;
};

const AssayTypesPage: React.FC<AssayTypesPageProps> = ({ registrationOnly = false }) => {
  const [types, setTypes] = useState<AssayType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<AssayType | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<AssayTypeInput>(EMPTY_FORM);
  const [protocolFor, setProtocolFor] = useState<AssayType | null>(null);
  const [protocol, setProtocol] = useState({ version_label: '1.0', protocol_text: '', change_note: '' });
  const [protocolFile, setProtocolFile] = useState<File | null>(null);
  const [definition, setDefinition] = useState<Record<string, any> | null>(null);
  const [extractionWarnings, setExtractionWarnings] = useState<string[]>([]);
  const [extracting, setExtracting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await assayRequestApi.fetchAdminAssayTypes();
      setTypes(data.types);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to load assay types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => {
    return Array.from(new Set([...STANDARD_CATEGORIES, ...types.map((type) => type.category)])).sort();
  }, [types]);

  const visibleTypes = useMemo(() => {
    const query = search.trim().toLowerCase();
    return types.filter((type) => {
      if (!showInactive && type.is_active === false) return false;
      if (!query) return true;
      return [type.name, type.category, type.description, type.typical_units]
        .some((value) => value?.toLowerCase().includes(query));
    });
  }, [types, search, showInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setModalOpen(true);
    setDefinition(null);
    setExtractionWarnings([]);
  };

  const openEdit = (type: AssayType) => {
    setEditing(type);
    setForm({
      name: type.name,
      category: type.category,
      description: type.description || '',
      typical_units: type.typical_units || '',
      input_mode: type.input_mode || 'dose_response',
      is_active: type.is_active !== false,
      internal_code: type.internal_code || '',
      target_name: type.target_name || '',
    });
    setError(null);
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.category.trim()) {
      setError('Name and category are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await assayRequestApi.updateAssayType(editing.id, form);
        setNotice(`${form.name.trim()} updated`);
      } else {
        const created = await assayRequestApi.createAssayType(form);
        setNotice(`${form.name.trim()} added to the assay catalogue`);
        setProtocolFor(created);
        setProtocol({ version_label: '1.0', protocol_text: '', change_note: '' });
        setProtocolFile(null);
        setDefinition(null);
        setExtractionWarnings([]);
      }
      setModalOpen(false);
      await load();
      window.setTimeout(() => setNotice(null), 2500);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to save assay type');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (type: AssayType) => {
    setError(null);
    try {
      await assayRequestApi.updateAssayType(type.id, {
        name: type.name,
        category: type.category,
        description: type.description,
        typical_units: type.typical_units,
        input_mode: type.input_mode || 'dose_response',
        is_active: type.is_active === false,
        internal_code: type.internal_code,
        target_name: type.target_name,
      });
      setNotice(type.is_active === false ? `${type.name} reactivated` : `${type.name} deactivated`);
      await load();
      window.setTimeout(() => setNotice(null), 2500);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to update assay type');
    }
  };

  const inputStyle: React.CSSProperties = {
    color: 'var(--text-primary)',
    backgroundColor: 'var(--content-bg)',
    borderColor: 'var(--card-border)',
  };

  const definitionValue = (path: string) => path.split('.').reduce((value: any, key) => value?.[key], definition) || '';
  const updateDefinition = (path: string, value: string) => {
    if (!definition) return;
    const clone = JSON.parse(JSON.stringify(definition));
    const keys = path.split('.');
    let cursor = clone;
    keys.slice(0, -1).forEach((key) => { cursor[key] = cursor[key] || {}; cursor = cursor[key]; });
    cursor[keys[keys.length - 1]] = value || null;
    setDefinition(clone);
  };

  const extractProtocol = async () => {
    if (!protocolFor || !protocolFile) return;
    setExtracting(true);
    setError(null);
    try {
      const result = await assayRequestApi.extractProtocolDocument(protocolFor.id, protocolFile);
      setDefinition(result.definition);
      setExtractionWarnings(result.warnings);
      setNotice(`Extracted ${result.paragraph_count} paragraphs and ${result.table_count} table${result.table_count === 1 ? '' : 's'} into a draft assay definition`);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Unable to extract the Word protocol'));
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Assay Registry</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Register each custom assay for everyone in this organisation. Its internal code and version-controlled Word protocol are shared tenant-wide, while historical runs retain the exact version used.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700"
        >
          + Register assay
        </button>
      </div>

      {(error || notice) && (
        <div
          className="mb-4 px-3 py-2 rounded-lg text-sm border"
          style={error
            ? { backgroundColor: '#fef2f2', color: '#991b1b', borderColor: '#fecaca' }
            : { backgroundColor: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}
        >
          {error || notice}
        </div>
      )}

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}>
        <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b" style={{ borderColor: 'var(--card-border)' }}>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search assay types..."
            className="w-72 max-w-full px-3 py-2 text-sm rounded-lg border"
            style={inputStyle}
          />
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
            Show inactive
          </label>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Loading assay types...</div>
        ) : visibleTypes.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No assay types match this view.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: 'var(--hover-bg)', color: 'var(--text-muted)' }}>
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Category</th>
                  <th className="text-left px-4 py-3 font-semibold">Protocol</th>
                  <th className="text-left px-4 py-3 font-semibold">Usage</th>
                  <th className="text-left px-4 py-3 font-semibold">Result format</th>
                  <th className="text-left px-4 py-3 font-semibold">Units</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleTypes.map((type) => (
                  <tr key={type.id} className="border-t" style={{ borderColor: 'var(--card-border)', color: 'var(--text-primary)' }}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{type.name}</div>
                      <div className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{type.internal_code || 'No internal code'}{type.target_name ? ` · ${type.target_name}` : ''}</div>
                      {type.description && <div className="text-xs mt-0.5 max-w-md" style={{ color: 'var(--text-muted)' }}>{type.description}</div>}
                    </td>
                    <td className="px-4 py-3">{type.category}</td>
                    <td className="px-4 py-3">{type.protocol_count || 0} version{type.protocol_count === 1 ? '' : 's'}</td>
                    <td className="px-4 py-3">{type.run_count || 0} runs · {type.compound_count || 0} compounds</td>
                    <td className="px-4 py-3">{INPUT_MODES.find((mode) => mode.value === type.input_mode)?.label || type.input_mode}</td>
                    <td className="px-4 py-3">{type.typical_units || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${type.is_active === false ? 'bg-gray-100 text-gray-600' : 'bg-emerald-100 text-emerald-700'}`}>
                        {type.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {!registrationOnly && <button type="button" onClick={() => openEdit(type)} className="px-3 py-1.5 rounded border text-xs font-medium hover:bg-gray-50" style={{ borderColor: 'var(--card-border)' }}>Edit</button>}
                        <button type="button" onClick={() => { setProtocolFor(type); setProtocol({ version_label: '', protocol_text: '', change_note: '' }); setProtocolFile(null); setDefinition(null); setExtractionWarnings([]); }} className="px-3 py-1.5 rounded border text-xs font-medium text-teal-700" style={{ borderColor: 'var(--card-border)' }}>Add protocol</button>
                        {!registrationOnly && <button
                          type="button"
                          onClick={() => toggleActive(type)}
                          className={`px-3 py-1.5 rounded border text-xs font-medium ${type.is_active === false ? 'text-emerald-700' : 'text-amber-700'}`}
                          style={{ borderColor: 'var(--card-border)' }}
                        >
                          {type.is_active === false ? 'Reactivate' : 'Deactivate'}
                        </button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editing ? 'Edit Assay Type' : 'Add Assay Type'}
        size="md"
      >
        <div className="space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg text-sm border bg-red-50 text-red-800 border-red-200">{error}</div>
          )}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Name *</label>
            <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border" style={inputStyle} placeholder="e.g. Lysosomal enzyme activity" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Internal assay code</label><input value={form.internal_code || ''} onChange={(e) => setForm({ ...form, internal_code: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border" style={inputStyle} placeholder="e.g. ANO-LYS-001" /></div>
            <div><label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Target</label><input value={form.target_name || ''} onChange={(e) => setForm({ ...form, target_name: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border" style={inputStyle} /></div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Category *</label>
            <input list="assay-category-options" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border" style={inputStyle} />
            <datalist id="assay-category-options">{categories.map((category) => <option key={category} value={category} />)}</datalist>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Description</label>
            <textarea value={form.description || ''} onChange={(event) => setForm({ ...form, description: event.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border min-h-[80px]" style={inputStyle} placeholder="What this assay measures" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Typical units</label>
              <input value={form.typical_units || ''} onChange={(event) => setForm({ ...form, typical_units: event.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border" style={inputStyle} placeholder="e.g. µM, nM, %" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Result format *</label>
              <select value={form.input_mode} onChange={(event) => setForm({ ...form, input_mode: event.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border" style={inputStyle}>
                {INPUT_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" disabled={saving} onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--card-border)', color: 'var(--text-secondary)' }}>Cancel</button>
            <button type="button" disabled={saving || !form.name.trim() || !form.category.trim()} onClick={save} className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50">
              {saving ? 'Saving...' : editing ? 'Save changes' : 'Add assay type'}
            </button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={!!protocolFor} onClose={() => !saving && setProtocolFor(null)} title={`Import protocol${protocolFor ? ` — ${protocolFor.name}` : ''}`} size="xl">
        <div className="space-y-4">
          <div><label className="block text-xs font-medium mb-1.5">Version *</label><input value={protocol.version_label} onChange={(e) => setProtocol({ ...protocol, version_label: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border" style={inputStyle} placeholder="e.g. 1.0 or 2026-09" /></div>
          <div className="rounded-lg border p-3" style={{ borderColor: 'var(--card-border)' }}><label className="block text-xs font-medium mb-2">Word protocol (.doc or .docx)</label><div className="flex flex-wrap items-center gap-3"><input type="file" accept=".doc,.docx" onChange={(e) => { setProtocolFile(e.target.files?.[0] || null); setDefinition(null); setExtractionWarnings([]); }} /><button type="button" disabled={extracting || !protocolFile || !protocolFile.name.toLowerCase().endsWith('.docx')} onClick={extractProtocol} className="px-3 py-2 rounded-lg text-xs font-semibold text-white bg-blue-600 disabled:opacity-40">{extracting ? 'Extracting…' : 'Extract assay information'}</button></div>{protocolFile?.name.toLowerCase().endsWith('.doc') && <p className="text-xs text-amber-700 mt-2">Older .doc files can be retained as attachments, but automatic extraction requires .docx.</p>}</div>
          {extractionWarnings.length > 0 && <div className="rounded-lg bg-amber-50 text-amber-800 p-3 text-xs"><b>Review needed</b><ul className="list-disc pl-5 mt-1">{extractionWarnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></div>}
          {definition && <div className="rounded-xl border p-4 space-y-4" style={{ borderColor: 'var(--card-border)', background: 'var(--content-bg)' }}>
            <div><h4 className="font-semibold text-sm">Structured assay draft</h4><p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Review and correct the extracted values before publishing this protocol version.</p></div>
            <div><label className="block text-xs font-medium mb-1">Purpose</label><textarea value={definitionValue('purpose')} onChange={(e) => updateDefinition('purpose', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border min-h-[70px]" style={inputStyle} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <StructuredField label="Target" value={definitionValue('biological_system.target')} onChange={(v) => updateDefinition('biological_system.target', v)} style={inputStyle} />
              <StructuredField label="Cell line / biological material" value={definitionValue('biological_system.cell_line')} onChange={(v) => updateDefinition('biological_system.cell_line', v)} style={inputStyle} />
              <StructuredField label="Plate format" value={definitionValue('method.plate_format')} onChange={(v) => updateDefinition('method.plate_format', v)} style={inputStyle} />
              <StructuredField label="Detection method" value={definitionValue('method.detection_method')} onChange={(v) => updateDefinition('method.detection_method', v)} style={inputStyle} />
              <StructuredField label="Instrument" value={definitionValue('method.instrument')} onChange={(v) => updateDefinition('method.instrument', v)} style={inputStyle} />
              <StructuredField label="Endpoint" value={definitionValue('readout.endpoint')} onChange={(v) => updateDefinition('readout.endpoint', v)} style={inputStyle} />
              <StructuredField label="Units" value={definitionValue('readout.units')} onChange={(v) => updateDefinition('readout.units', v)} style={inputStyle} />
              <StructuredField label="Incubation time" value={definitionValue('conditions.incubation_time')} onChange={(v) => updateDefinition('conditions.incubation_time', v)} style={inputStyle} />
            </div>
            {[['Assay principle','method.principle'],['Conditions','conditions.details'],['Procedure','procedure.source_text'],['Data analysis','analysis.method'],['QC acceptance criteria','quality_control.acceptance_criteria']].map(([label, path]) => <div key={path}><label className="block text-xs font-medium mb-1">{label}</label><textarea value={definitionValue(path)} onChange={(e) => updateDefinition(path, e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border min-h-[70px]" style={inputStyle} /></div>)}
          </div>}
          <div><label className="block text-xs font-medium mb-1.5">Protocol summary</label><textarea value={protocol.protocol_text} onChange={(e) => setProtocol({ ...protocol, protocol_text: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border min-h-[100px]" style={inputStyle} /></div>
          <div><label className="block text-xs font-medium mb-1.5">Version / change note</label><input value={protocol.change_note} onChange={(e) => setProtocol({ ...protocol, change_note: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border" style={inputStyle} /></div>
          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Publishing makes this version current. The reviewed structured record and original Word document are both retained; older versions remain available to historical runs.</div>
          <div className="flex justify-end gap-2"><button onClick={() => setProtocolFor(null)} className="px-4 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--card-border)' }}>Cancel</button><button disabled={saving || !protocol.version_label.trim() || (!protocolFile && !protocol.protocol_text.trim()) || (!!protocolFile?.name.toLowerCase().endsWith('.docx') && !definition)} onClick={async () => { if (!protocolFor) return; setSaving(true); setError(null); try { await assayRequestApi.uploadProtocolVersion(protocolFor.id, { ...protocol, document: protocolFile, structured_definition: definition, extraction_warnings: extractionWarnings }); setNotice(`Protocol ${protocol.version_label} published for ${protocolFor.name}`); setProtocolFor(null); await load(); } catch (err: any) { setError(apiErrorMessage(err, 'Unable to upload protocol')); } finally { setSaving(false); } }} className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-teal-600 disabled:opacity-50">{saving ? 'Publishing…' : 'Publish reviewed version'}</button></div>
        </div>
      </Modal>
    </div>
  );
};

const StructuredField: React.FC<{ label: string; value: string; onChange: (value: string) => void; style: React.CSSProperties }> = ({ label, value, onChange, style }) => <div><label className="block text-xs font-medium mb-1">{label}</label><input value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border" style={style} /></div>;

export default AssayTypesPage;
